import type { ClassWeekCacheKey, KioskClassWeekCell } from "../types/kioskApiTypes";
import { useKioskGetLessonBundleQuery } from "../api/klassenbuchKioskApi";
import { useKioskConfirmAttendance } from "../hooks/useKioskAttendanceSave";
import { shortName, STATUS_BUTTONS } from "../utils/classWeekHelpers";
import { AttendanceDialog } from "../../attendance/components/AttendanceDialog";
import type { AttendanceBundle } from "../../../lib/attendanceBundle";

// The kiosk's 4-status model has no `absent_unclear` — there's only
// "U" (absent_unexcused). The DB has migrated to writing
// `absent_unclear` everywhere (live data check 2026-05-20: 26902
// absent_unclear vs 0 absent_unexcused rows in student_attendance_logs).
// Map both into the kiosk's single U bucket here so ungeklärt
// students show up in the cell chips + counts instead of being
// silently dropped. The write boundary (api/mutations.ts) does the
// reverse mapping when saving.
function normalizeAbsenceStatus(status: string) {
  if (status === "excused") return "absent_excused";
  if (status === "unexcused" || status === "absent_unclear" || status === "unclear") return "absent_unexcused";
  return status;
}

function isAbsenceLikeStatus(status: string) {
  const normalized = normalizeAbsenceStatus(status);
  return (
    normalized === "late" ||
    normalized === "absent_excused" ||
    normalized === "absent_unexcused" ||
    normalized === "beurlaubung"
  );
}

function normalizeAttendanceStatus(status: string) {
  if (status === "excused") return "absent_excused";
  if (status === "unexcused" || status === "absent_unclear" || status === "unclear") return "absent_unexcused";
  return status;
}

export function CellAttendance({
  cell,
  userId,
  classWeekKey,
  open,
  onOpen,
  onClose,
}: {
  cell: KioskClassWeekCell;
  userId: string;
  classWeekKey: ClassWeekCacheKey;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const { handleConfirm, isConfirming, confirmError } = useKioskConfirmAttendance(
    cell,
    userId,
    classWeekKey,
  );
  const { data: lessonBundle, refetch: refetchLessonBundle } = useKioskGetLessonBundleQuery(
    { lessonId: cell.lesson_id, userId },
    { skip: !(open || cell.attendance_recorded) },
  );

  // No attendance for lessons that haven't started yet — but allow recording
  // up to 10 min before the official start (matches chip-login tolerance).
  const hasStarted = new Date(cell.start_at).getTime() - 10 * 60_000 <= Date.now();
  if (!hasStarted) {
    return (
      <div className="mt-0.5 text-[10px] text-slate-400 italic">
        Stunde noch nicht begonnen
      </div>
    );
  }

  // What we render in both states ↓
  // - attendance_recorded=true  → show cell.absentees (final state) + green
  //                                 "Anwesenheit erfasst ✎" edit button
  // - attendance_recorded=false → show cell.seed_absentees (carry-forward
  //                                 from prior lesson) + amber "Bestätigen"
  //                                 + small "Bearbeiten" button
  const recorded = cell.attendance_recorded;
  const list = recorded ? (cell.absentees ?? []) : (cell.seed_absentees ?? []);
  const absenceEntries = list.filter((a) => isAbsenceLikeStatus(a.status));
  const late = absenceEntries.filter((a) => normalizeAbsenceStatus(a.status) === "late");
  const others = absenceEntries.filter((a) => normalizeAbsenceStatus(a.status) !== "late");
  const recordedCounts = (() => {
    if (!recorded || !lessonBundle) return null;
    const byStudent = new Map<string, string>();
    for (const rec of lessonBundle.currentAttendance) {
      byStudent.set(rec.student_id, normalizeAttendanceStatus(rec.status));
    }
    let present = 0;
    let lateCount = 0;
    let absent = 0;
    for (const student of lessonBundle.students) {
      const st = byStudent.get(student.id) ?? "present";
      if (st === "late") lateCount += 1;
      else if (
        st === "absent_excused" ||
        st === "absent_unexcused" ||
        st === "beurlaubung"
      ) absent += 1;
      else present += 1;
    }
    return { present, late: lateCount, absent };
  })();

  // "Anwesend" tally — accurate counts when recorded, otherwise derived
  // from class size minus the (non-late) absences carried forward.
  const presentCount = recorded
    ? (recordedCounts?.present ?? null)
    : cell.student_count != null
      ? Math.max(0, cell.student_count - others.length)
      : null;

  return (
    <div className="mt-0.5 flex flex-col gap-1">
      {late.length > 0 ? (
        <ul className="grid grid-cols-3 gap-x-2 gap-y-[1px]">
          {late.map((a) => (
            <li
              key={a.student_id}
              className="flex items-center gap-1 text-[10px] leading-tight text-slate-800"
              title={a.notes ? `${a.name} — ${a.notes}` : a.name}
            >
              <span className="px-1 h-4 min-w-[1.6rem] rounded text-[9px] font-medium leading-none flex items-center justify-center flex-shrink-0 bg-amber-400 text-amber-950">
                🕐
              </span>
              <span className="truncate flex-1 text-[12px]">{shortName(a.name)}</span>
              {a.late_minutes != null ? (
                <span className="text-[9px] text-slate-500 whitespace-nowrap">
                  +{a.late_minutes}′
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {!recorded && others.length > 0 ? (
        <ul className="grid grid-cols-3 gap-x-2 gap-y-[1px]">
          {others.map((a) => {
            const normalizedStatus = normalizeAbsenceStatus(a.status);
            const meta = STATUS_BUTTONS.find((b) => b.key === normalizedStatus);
            const fallbackBadge =
              normalizedStatus === "absent_excused"
                ? { label: "Inf", tone: "bg-sky-500 text-white" }
                : normalizedStatus === "absent_unexcused"
                ? { label: "U", tone: "bg-rose-500 text-white" }
                : null;
            return (
              <li
                key={a.student_id}
                className="flex items-center gap-1 text-[10px] leading-tight text-slate-800"
                title={a.notes ? `${a.name} — ${a.notes}` : a.name}
              >
                {meta || fallbackBadge ? (
                  <span
                    className={`px-1 h-4 min-w-[1.6rem] rounded text-[9px] font-medium leading-none flex items-center justify-center flex-shrink-0 ${meta ? meta.activeTone : fallbackBadge?.tone}`}
                  >
                    {meta ? meta.label : fallbackBadge?.label}
                  </span>
                ) : null}
                <span className="truncate flex-1 text-[12px]">{shortName(a.name)}</span>
                {a.notes ? (
                  <span className="text-slate-400 cursor-help" aria-label={a.notes}>ⓘ</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
        {presentCount != null ? (
          <span className="text-[12px] font-bold text-emerald-700">
            {presentCount} anwesend
          </span>
        ) : null}
        {recorded ? (
          <button
            onClick={onOpen}
            className="ml-auto inline-flex items-center justify-center min-w-[90px] px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100/90 text-emerald-700 hover:bg-emerald-200/80 transition-colors"
            title="Anwesenheit bearbeiten"
          >
            <span>
              {recordedCounts
                ? `${recordedCounts.present}/${recordedCounts.late}/${recordedCounts.absent}`
                : "Vollständig"}
            </span>
            <span aria-hidden>✓</span>
          </button>
        ) : (
          <div className="flex items-center gap-1 ml-auto">
            {absenceEntries.length > 0 && (
              <button
                onClick={() => handleConfirm(absenceEntries)}
                disabled={isConfirming}
                className="inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border border-amber-300 bg-amber-100 text-amber-700 hover:bg-amber-200/80 transition-colors font-medium disabled:opacity-50"
              >
                {isConfirming ? "…" : "Bestätigen"}
              </button>
            )}
            <button
              onClick={onOpen}
              className="inline-flex items-center justify-center min-w-[90px] px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
            >
              Anpassen
            </button>
          </div>
        )}
      </div>
      <AttendanceDialog
        // Critical: gate `open` on the bundle being loaded too. The kiosk
        // bundle query is `skip: !(open || cell.attendance_recorded)`, so
        // when the user clicks "Eintragen" we go from `open=false,
        // bundle=undefined` straight to `open=true, bundle=still loading`.
        // If we passed `open` through directly, AttendanceDialog would
        // mount with `initialBundle={null}` and `useAttendanceLoader`
        // would bail on first render (no auth-context school_id, no
        // initialBundle) — and its load effect does not re-run when the
        // bundle later arrives. Result: empty dialog body, exactly the
        // bug we thought we fixed.
        //
        // By gating on `lessonBundle?._raw` we delay the open by the few
        // hundred ms the bundle fetch takes. The trigger button has its
        // own loading state via `isConfirming`, so the small delay
        // doesn't surprise the user. Closes (Copilot review on PR #620).
        open={open && !!lessonBundle?._raw}
        onOpenChange={(nextOpen) => {
          if (nextOpen) onOpen();
          else onClose();
        }}
        lessonId={cell.lesson_id}
        viewMode="edit"
        // The cell's own "Anpassen" / "Bestätigen" buttons above already
        // surface the carry-forward state — opening the dialog should
        // land directly in the edit grid, not show the same summary card
        // a second time. Matches the Klassenbuch diary view wiring.
        skipPrefillSummary={true}
        // Hand the dialog the bundle we already fetched. Without this,
        // useAttendanceLoader inside the dialog tries to pull school_id
        // from the React auth context — which on a paired kiosk is null,
        // because there's no signed-in user — and bails before loading
        // anything, leaving the editor body empty (only the header
        // renders). See KioskLessonBundle._raw in kioskApiTypes.ts for
        // why we carry the raw shape through.
        initialBundle={(lessonBundle?._raw as AttendanceBundle) ?? null}
        // Same reasoning as initialBundle: in paired-kiosk mode there's
        // no signed-in React user, so any secondary auth-context read
        // (save's recorded_by, loader's reloadBundle, realtime sub,
        // TagesgastCard's resolver_staff_id) ends up null.
        //
        // userIdOverride supplies the teacher (from the chip-login
        // session) for every consumer in the dialog tree.
        // schoolIdOverride supplies the kiosk school for the loader's
        // secondary fetch paths. The initial bundle is already covered
        // by initialBundle.
        userIdOverride={userId}
        schoolIdOverride={(lessonBundle?._raw as AttendanceBundle | null)?.lesson?.school_id ?? null}
        onSaved={() => {
          try {
            void refetchLessonBundle();
          } catch {
            // Query may not be started yet in edge timing cases; class-week
            // refresh event below still updates the visible cell state.
          }
          window.dispatchEvent(
            new CustomEvent("kiosk-attendance-saved", {
              detail: { lessonId: cell.lesson_id },
            }),
          );
          onClose();
        }}
      />
      {confirmError ? (
        <div className="text-[10px] text-rose-600">⚠ {confirmError}</div>
      ) : null}
    </div>
  );
}
